import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api';

interface SessionItem {
  readonly id: string;
  readonly deviceLabel: string | null;
  readonly userAgent: string | null;
  readonly createdAt: string;
  readonly lastSeenAt: string;
  readonly isCurrent: boolean;
}

/** 我的会话与设备（UX S15）。吊销后立即失效——下一次该设备的请求就会 401。 */
export function SessionsPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['sessions', 'me'],
    queryFn: () => apiFetch<{ items: SessionItem[] }>('/sessions'),
  });

  const revoke = async (id: string): Promise<void> => {
    if (!confirm(t('settings.sessionRevokeConfirm'))) return;
    await apiFetch(`/sessions/${id}/revoke`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['sessions', 'me'] });
  };

  return (
    <section className="section">
      <h2>{t('settings.sessionsTitle')}</h2>
      {isPending && <p>{t('app.loading')}</p>}
      {data && data.items.length === 0 && (
        <p className="field-hint">{t('settings.sessionsNone')}</p>
      )}
      {data && data.items.length > 0 && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('settings.sessionDevice')}</th>
              <th>{t('settings.sessionCreated')}</th>
              <th>{t('settings.sessionLastActive')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id}>
                <td>{s.deviceLabel ?? s.userAgent ?? '—'}</td>
                <td>{new Date(s.createdAt).toLocaleString()}</td>
                <td>{new Date(s.lastSeenAt).toLocaleString()}</td>
                <td>
                  {s.isCurrent ? (
                    <span className="badge badge--muted">{t('settings.sessionThisDevice')}</span>
                  ) : (
                    <button className="btn btn--danger btn--sm" onClick={() => void revoke(s.id)}>
                      {t('settings.sessionRevoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </section>
  );
}
